import { useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useBrand } from "@/context/BrandContext";
import { api, formatApiError, isPlaceholder } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";

export default function AuthPage() {
  const { login, refreshMe } = useAuth();
  const { edition } = useBrand();
  const navigate = useNavigate();
  const location = useLocation();
  const [form, setForm] = useState({ email: "", password: "" });
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      const u = await login(form.email, form.password);
      const track = location.state?.track;
      if (track) {
        await api.patch("/auth/track", { track });
        await refreshMe();
        return navigate("/dashboard");
      }
      navigate(location.state?.from || (u.role === "admin" ? "/admin" : "/dashboard"));
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid min-h-[calc(100vh-4rem)] lg:grid-cols-2">
      <div className="hidden flex-col justify-between bg-trust p-12 text-white lg:flex">
        <div className="inline-block w-fit rounded-sm bg-white p-2"><Logo edition={edition} className="h-8" testId="auth-brand-logo" /></div>
        <div>
          <div className="font-mono text-xs uppercase tracking-widest text-white/60">{edition?.edition_label}</div>
          <p className="mt-4 max-w-md font-serif text-3xl italic leading-snug">“This answer is only as strong as its asset, time window, and evidence.”</p>
        </div>
        <div className="text-xs text-white/50">Foundation → Practitioner → Strategist</div>
      </div>
      <div className="flex items-center justify-center px-4 py-16">
        <form onSubmit={submit} data-testid="login-form" className="fade-up w-full max-w-sm space-y-5">
          <div>
            <div className="overline-label">Welcome back</div>
            <h1 className="mt-2 font-heading text-3xl font-black tracking-tight">Sign in</h1>
            {location.state?.track && <p data-testid="auth-track-note" className="mt-2 text-sm text-muted-foreground">After sign-in you'll go straight to the {location.state.track === "executive" ? "Executive Briefing" : "full programme"}.</p>}
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">Email</Label>
            <Input id="email" data-testid="auth-email-input" type="email" required autoComplete="email" className="h-11 rounded-sm" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
          </div>
          <div className="space-y-2">
            <Label htmlFor="password">Password</Label>
            <div className="relative">
              <Input id="password" data-testid="auth-password-input" type={show ? "text" : "password"} required autoComplete="current-password" className="h-11 rounded-sm pr-11" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              <button type="button" data-testid="auth-password-toggle" onClick={() => setShow(!show)} aria-label={show ? "Hide password" : "Show password"} className="absolute right-0 top-0 flex h-11 w-11 items-center justify-center text-muted-foreground hover:text-foreground">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          {error && <p data-testid="auth-error" role="alert" className="text-sm text-destructive">{error}</p>}
          <button type="submit" disabled={busy} data-testid="auth-submit-button" className="h-11 w-full rounded-sm bg-brand-primary text-sm font-semibold text-brand-primary-fg transition-opacity hover:opacity-90 disabled:opacity-60">{busy ? "Please wait…" : "Sign in"}</button>
          <p data-testid="auth-access-note" className="text-sm text-muted-foreground">Access is provided by your administrator. Contact {edition?.support_email && !isPlaceholder(edition.support_email) ? edition.support_email : "your administrator"} if you need an account.</p>
        </form>
      </div>
    </div>
  );
}
